import { useState } from "react"
import Input from "@/v2-ui/input"
import { toast } from "@/v2-ui/toast"
import Button, { ConfirmButton } from "@/v2-backoffice-client/components/button"
import {
  ENGLISH_LANGUAGE_CODE,
  LANGUAGE_DISPLAY_ORDER,
} from "@/v2-backoffice-client/translation/translation.constants"
import { useTranslationUpdateCountryMutation } from "@/v2-backoffice-client/translation/countries/translation.updateCountry.mutation"
import {
  COUNTRY_PUBLISH_NOTE,
  COUNTRY_PUBLISH_TOAST,
} from "@/v2-backoffice-client/translation/countries/translation.countries.constants"
import type { TranslationCountryRow } from "@/v2-backoffice-client/translation/countries/translation.countries.utils"

export type TranslationCountriesDetailProps = {
  row: TranslationCountryRow
}

// Supported languages first in the tab's usual order, then any language the
// catalog still carries but no longer supports (bn-BD is the live example) —
// those must stay visible, because a save replaces the whole name map and
// hiding one would delete it.
function languageOrder(row: TranslationCountryRow) {
  const codes = new Set([
    ...row.names.map((name) => name.languageCode),
    ...row.missingLanguages,
  ])
  return [...codes].sort((left, right) => {
    const leftRank = LANGUAGE_DISPLAY_ORDER.indexOf(left)
    const rightRank = LANGUAGE_DISPLAY_ORDER.indexOf(right)
    if (leftRank !== rightRank) {
      if (leftRank === -1) return 1
      if (rightRank === -1) return -1
      return leftRank - rightRank
    }
    return left.localeCompare(right)
  })
}

/**
 * The expanded country row: its names per language, and the IsActive flag.
 *
 * Names live in location.Countries.[Language], not in common.Translations —
 * countries are not registered in common.TypeSets — so this is their only
 * editor. None of the translation tooling (the missing-translations queue,
 * the AI fills) reaches them.
 */
function TranslationCountriesDetail(props: TranslationCountriesDetailProps) {
  const { row } = props

  const codes = languageOrder(row)
  const saved: Record<string, string> = Object.fromEntries(
    row.names.map((name) => [name.languageCode, name.name]),
  )
  const [draft, setDraft] = useState<Record<string, string> | null>(null)
  const names = draft ?? saved

  const [updateCountry, updateCountryState] =
    useTranslationUpdateCountryMutation({
      onCompleted() {
        setDraft(null)
      },
    })

  const isDirty =
    draft !== null && codes.some((code) => names[code] !== saved[code])
  const hasEnglish = (names[ENGLISH_LANGUAGE_CODE] ?? "").trim() !== ""

  async function save() {
    try {
      await updateCountry({
        variables: {
          input: {
            Code: row.code,
            // Empty fields drop the language rather than storing a blank —
            // the catalog's own reads treat a missing key and an empty name
            // the same, and a blank one would count as "named" here.
            Names: codes
              .filter((code) => (names[code] ?? "").trim() !== "")
              .map((code) => ({
                LanguageCode: code,
                Name: names[code].trim(),
              })),
          },
        },
      })
      toast({
        title: `${row.name} names saved.`,
        description: COUNTRY_PUBLISH_TOAST,
      })
    } catch (error) {
      toast({
        variant: "destructive",
        title: `Failed to save ${row.name}`,
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  async function setActive(isActive: boolean) {
    try {
      await updateCountry({
        variables: {
          input: {
            Code: row.code,
            IsActive: isActive,
            // Retiring drops the country from every list the runtime reads,
            // so leaving IsSupported on would have the registry claiming a
            // market that no longer resolves. Coming back is not symmetric:
            // reopening a market is the Supported toggle's call, not a
            // side effect of un-retiring.
            ...(!isActive && row.isSupported ? { IsSupported: false } : {}),
          },
        },
      })
      toast({
        title: `${row.name} is now ${isActive ? "active" : "retired"}.`,
        description: COUNTRY_PUBLISH_TOAST,
      })
    } catch (error) {
      toast({
        variant: "destructive",
        title: `Failed to update ${row.name}`,
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <section className="flex flex-col gap-3">
        <header className="flex items-baseline gap-2">
          <h3 className="text-sm font-medium">Names</h3>
          <p className="text-xs text-muted-foreground">
            What the country is called in each language. English is the anchor
            and cannot be left empty.
          </p>
        </header>

        <div className="flex max-w-xl flex-col gap-2">
          {codes.map((code) => (
            <label key={code} className="flex items-center gap-3">
              <span className="w-20 shrink-0 font-mono text-xs text-muted-foreground">
                {code}
              </span>
              <Input
                value={names[code] ?? ""}
                placeholder={`Name in ${code}`}
                disabled={updateCountryState.loading}
                onChange={(event) =>
                  setDraft({ ...names, [code]: event.target.value })
                }
              />
            </label>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Button
            size="sm"
            disabled={!isDirty || !hasEnglish}
            isLoading={updateCountryState.loading}
            data-feature-code="UI_BackofficeTranslation_Countries_SaveNames"
            onClick={() => void save()}
          >
            Save names
          </Button>
          {isDirty && !hasEnglish ? (
            <p className="text-xs text-destructive">
              {ENGLISH_LANGUAGE_CODE} is required.
            </p>
          ) : null}
        </div>
      </section>

      <section className="flex flex-col gap-2 border-t pt-4">
        <header className="flex items-baseline gap-2">
          <h3 className="text-sm font-medium">Active</h3>
          <p className="text-xs text-muted-foreground">
            Retiring a country removes it from every list the apps read — the
            pickers, the availability lists and country validation — not just
            from the markets. Records already pointing at it still resolve to a
            name, because lookups by code are deliberately not filtered.
          </p>
        </header>
        <div>
          <ConfirmButton
            size="sm"
            variant="outline"
            disabled={updateCountryState.loading}
            confirmContent={
              row.isActive
                ? `Retire ${row.name}? It leaves the pickers, the availability lists and country validation entirely.${row.isSupported ? " It is a supported market today, so this closes that market too." : ""} ${COUNTRY_PUBLISH_NOTE}`
                : `Bring ${row.name} back into the catalog? It does not reopen as a market — that is the Supported toggle's call. ${COUNTRY_PUBLISH_NOTE}`
            }
            confirmVariant={row.isActive ? "destructive" : undefined}
            onConfirm={() => setActive(!row.isActive)}
            data-feature-code="UI_BackofficeTranslation_Countries_ToggleActive"
          >
            {row.isActive ? "Deactivate" : "Reactivate"}
          </ConfirmButton>
        </div>
      </section>
    </div>
  )
}

export default TranslationCountriesDetail
